import React, { AriaAttributes, FC, ReactNode, useMemo } from 'react';
import { useId, usePress } from 'react-aria';
import { Loggable, useLogger } from '@direct-frontend/modules-logger';
import { setInteractionModality } from '@react-aria/interactions';

import { TextGenericWithSkin } from '../TextGeneric/TextGenericWithSkin';
import { cn } from '../../lib/css/className/className';
import { getDataAttributesProps } from '../../lib/react/getDataAttributesProps/getDataAttributesProps';
import { HStack, VStack } from '../Stack';
import { ErrorLogger } from '../ErrorLogger/ErrorLogger';
import { ElementHeader, ElementHeaderProps } from '../ElementHeader';
import { TestProps } from '../../internal/types/testTypes';
import { type DataAttributesProps } from '../../internal/types/dataAttributesTypes';
import { getSizeSkin } from '../../lib/skin/getSizeSkin/getSizeSkin';

import './FormField.css';

const cls = cn('FormField');

export interface FormFieldAccessibilityProps extends AriaAttributes {
    /**
     * Id поля
     * @default useId()
     */
    id?: string;

    /**
     * Id подписи к полю
     * @default useId()
     */
    labelId?: string;

    /**
     * Id сообщения об ошибке к полю
     * @default useId()
     */
    errorMessageId?: string;

    /**
     * Id описания поля
     * @default useId()
     */
    descriptionId?: string;
}

export interface FormFieldControlProps extends FormFieldAccessibilityProps {
    /**
     * Состояние поля (влияет на внешний вид)
     */
    validationState?: 'valid' | 'invalid';

    /**
     * Признак отключения поля (будут изменены стили label)
     * @default false
     */
    isDisabled?: boolean;

    /**
     * Признак нахождения в состоянии загрузки (будут изменены стили label, сообщение об ошибке будет скрыто)
     * @default false
     */
    isLoading?: boolean;

    /**
     * Признак обязательности поля
     * @default false
     */
    isRequired?: boolean;

    /**
     * Текстовый лейбл
     */
    label?: string;

    /**
     * Названия классов для элемента
     */
    className?: string;
}

export type FormFieldProps = TestProps &
    DataAttributesProps &
    Loggable &
    Omit<ElementHeaderProps, 'children'> & {
        /**
         * Названия классов для элемента
         */
        className?: string;

        /**
         * Дочерний элемент, в качестве которого может быть `TextInput`, `Select`
         * и другие компоненты, использующие `FormFieldControlProps`).
         */
        children: (controlProps: FormFieldControlProps) => ReactNode;

        /**
         * Признак отключения поля (изменены стили label)
         * @default false
         */
        isDisabled?: boolean;

        /**
         * Признак нахождения в состоянии загрузки (будут изменены стили label, сообщение об ошибке будет скрыто)
         * @default false
         */
        isLoading?: boolean;

        /**
         * Признак обязательности поля
         * @default false
         */
        isRequired?: boolean;

        /**
         * Состояние поля (влияет на внешний вид)
         */
        validationState?: 'valid' | 'invalid';

        /**
         * Подпись к полю (заголовок).
         * Тег `label` и необходимые для него свойства будут добавлены автоматически.
         * Пользовательский компонент рекомендуется собирать из компонентов библиотеки
         * для корректного изменения стилей при disabled-состоянии
         */
        label?: ReactNode;

        /**
         * Сообщение об ошибке
         */
        errorMessage?: ReactNode;

        /**
         * Описание поля (под полем).
         * Пользовательский компонент рекомендуется собирать из компонентов библиотеки
         * для корректного изменения стилей при disabled-состоянии
         */
        description?: ReactNode;

        /**
         * Код ошибки
         */
        errorCode?: string;

        /**
         * Значение отступов между элементами поля
         * @default 6
         */
        gap?: '6' | '4';

        /**
         * Признак горизонтального расположения элементов поля
         * @default false
         */
        horizontalLayout?: boolean;

        /**
         * Бэйдж
         */
        badge?: ReactNode;
    };

type HorizontalLayoutProps = {
    children: ReactNode;
    textAddonRight?: ReactNode;
    addonRight?: ReactNode;
    description?: ReactNode;
    error?: ReactNode;
    size: FormFieldProps['size'];
};

const HorizontalLayout = function HorizontalLayout({
    children,
    addonRight,
    textAddonRight,
    description,
    error,
    size = 'm',
}: HorizontalLayoutProps) {
    return (
        <VStack gap="6" className={cls('horizontalLayout')}>
            <HStack gap="current-geometry">
                {children}
                {addonRight && <div className={cls('addonRight', { size })}>{addonRight}</div>}
                {textAddonRight && (
                    <TextGenericWithSkin textSize={'current-body'} skinSize={size} color="gray" role="supplementary">
                        {textAddonRight}
                    </TextGenericWithSkin>
                )}
            </HStack>
            {description}
            {error}
        </VStack>
    );
};

/**
 * Компонент для добавления подписей, описания и сообщения об ошибке
 * компактным и обычным компонентам `Select`, `TextInput` и т.д.
 */
export const FormField: FC<FormFieldProps> = function FormField({
    className,
    children,
    isDisabled,
    isLoading,
    isRequired,
    validationState,
    label,
    isBold,
    caption,
    help,
    addonRight,
    textAddonRight,
    errorMessage,
    description,
    errorCode,
    logNode = 'FormField',
    horizontalLayout = false,
    gap = '6',
    badge,
    size = 'm',
    ['data-testid']: dataTestId = 'FormField',
    ...props
}) {
    // генерация id для aria-свойств компонента
    const fieldId = useId();
    const labelId = useId();
    const errorMessageId = useId();
    const descriptionId = useId();

    // управление отображением ошибок
    const showError = errorMessage && !isLoading;

    const sizeSkinClass = getSizeSkin(size);

    const [elementHeaderProps, horizontalLayoutProps] = useMemo<
        Pick<ElementHeaderProps, 'addonRight' | 'textAddonRight'>[]
    >(() => {
        if (horizontalLayout) {
            return [
                {},
                {
                    addonRight,
                    textAddonRight,
                },
            ];
        }

        return [
            {
                addonRight,
                textAddonRight,
            },
            {},
        ];
    }, [addonRight, horizontalLayout, textAddonRight]);

    // подготовка компонентов для отображения подписи, описания и сообщения об ошибках
    const labelComponent = useMemo(() => {
        return (
            <ElementHeader
                horizontalLayout={horizontalLayout}
                color={isDisabled || isLoading ? 'gray-disabled' : 'gray'}
                isBold={isBold}
                caption={caption}
                help={help}
                badge={badge}
                size={size}
                {...elementHeaderProps}
            >
                {label}
            </ElementHeader>
        );
    }, [horizontalLayout, isDisabled, isLoading, isBold, caption, help, badge, elementHeaderProps, size, label]);

    const { LogProvider } = useLogger(dataTestId, logNode);
    const logNodeName = typeof logNode !== 'string' ? logNode.name : logNode;

    const descriptionComponent = useMemo(() => {
        if (!description) {
            return null;
        }

        return (
            <div id={descriptionId} className={cls('description', { disabled: isDisabled })}>
                <TextGenericWithSkin
                    textSize="current-main"
                    skinSize={size}
                    role="supplementary"
                    color={isDisabled || isLoading ? 'gray-disabled' : 'gray'}
                    data-testid={dataTestId ? `${dataTestId}.description` : undefined}
                >
                    {description}
                </TextGenericWithSkin>
            </div>
        );
    }, [dataTestId, description, descriptionId, size, isDisabled, isLoading]);

    const errorComponent = useMemo(() => {
        return showError && !isLoading ? (
            <div
                id={errorMessageId}
                className={cls('error-message')}
                data-testid={dataTestId ? `${dataTestId}.error` : undefined}
            >
                <ErrorLogger
                    children={
                        <TextGenericWithSkin
                            textSize="current-main"
                            skinSize={size}
                            color="red"
                            data-testid={`${dataTestId}.error.text`}
                        >
                            {errorMessage}
                        </TextGenericWithSkin>
                    }
                    name={`${logNodeName || dataTestId}Error`}
                    errorCode={errorCode || ''}
                />
            </div>
        ) : null;
    }, [dataTestId, errorCode, errorMessage, errorMessageId, size, isLoading, logNodeName, showError]);

    const labelString = typeof label === 'string' ? label : undefined;

    const childrenComponent = useMemo(() => {
        return children({
            id: fieldId,
            labelId: label ? labelId : undefined,
            errorMessageId: showError ? errorMessageId : undefined,
            descriptionId: description ? descriptionId : undefined,
            isDisabled: isDisabled,
            isLoading: isLoading,
            validationState: showError ? 'invalid' : validationState,
            'aria-label': labelString,
            'aria-invalid': Boolean(showError) || validationState === 'invalid',
            'aria-required': isRequired,
            'aria-labelledby': label ? labelId : undefined,
            'aria-errormessage': showError ? errorMessageId : undefined,
            'aria-describedby':
                [label ? labelId : undefined, description ? descriptionId : undefined, showError && errorMessageId]
                    .filter(Boolean)
                    .join(' ') || undefined,
            ...(labelString ? { label: labelString } : {}),
            ...(horizontalLayout ? { className: cls('control') } : {}),
        });
    }, [
        children,
        fieldId,
        label,
        labelId,
        showError,
        errorMessageId,
        description,
        descriptionId,
        isDisabled,
        isLoading,
        validationState,
        horizontalLayout,
        labelString,
        isRequired,
    ]);

    // usePress нужен, чтобы отменить клик и чтобы в модалках не рисовался лишний outline
    const { pressProps } = usePress({
        onPress: () => {
            setInteractionModality('pointer');
        },
        isDisabled: isDisabled || isLoading,
        allowTextSelectionOnPress: true,
    });

    const ElementTag = horizontalLayout ? HStack : VStack;

    return (
        <LogProvider>
            <ElementTag
                {...getDataAttributesProps(props)}
                gap={horizontalLayout ? 'current-geometry' : gap}
                className={cls({}, [sizeSkinClass, className])}
                data-testid={dataTestId}
            >
                {label && (
                    <label
                        {...pressProps}
                        id={labelId}
                        aria-label={labelString}
                        htmlFor={fieldId}
                        className={cls('label', {
                            disabled: isDisabled || isLoading,
                            horizontal: horizontalLayout,
                        })}
                        data-testid={`${dataTestId}.label`}
                    >
                        {labelComponent}
                    </label>
                )}
                {horizontalLayout ? (
                    <HorizontalLayout
                        children={childrenComponent}
                        description={descriptionComponent}
                        error={errorComponent}
                        size={size}
                        {...horizontalLayoutProps}
                    />
                ) : (
                    <>
                        {childrenComponent}
                        {descriptionComponent}
                        {errorComponent}
                    </>
                )}
            </ElementTag>
        </LogProvider>
    );
};
